import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Avatar,
  Button,
} from "@mui/material";
import {
  HandshakeOutlined as PartnerIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  Language as GlobeIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { colors, borderRadiusPx } from "../../../../../theme";
import {
  getExplorerAddressUrl,
  EXPLORER_LABEL,
} from "../../../../../static/Constants";

const LotteryPartnerCard = ({ partner, partnerLogoUrl }) => {
  const [copied, setCopied] = useState(false);

  if (!partner || !partner.partnerAddress || partner.partnerAddress === "0x0000000000000000000000000000000000000000") {
    return null;
  }

  const handleCopy = () => {
    if (!partner.partnerAddress) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(partner.partnerAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Partner address copied!");
    }
  };

  const explorerUrl = getExplorerAddressUrl(partner.partnerAddress);
  const websiteUrl = partner.websiteAdd
    ? partner.websiteAdd.startsWith("http")
      ? partner.websiteAdd
      : `https://${partner.websiteAdd}`
    : null;

  return (
    <Card
      className="lottery-glass-card"
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.purple[50],
        border: `1px solid ${colors.purple[200]} !important`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        {/* Partner Info */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            src={partnerLogoUrl}
            alt={partner.name || "Partner"}
            sx={{
              width: 52,
              height: 52,
              borderRadius: borderRadiusPx.md,
              bgcolor: colors.purple[100],
              border: `1px solid ${colors.purple[300]}`,
            }}
          >
            <PartnerIcon sx={{ color: colors.purple[700] }} />
          </Avatar>

          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "1.05rem" }}>
                {partner.name || "Official Partner Organization"}
              </Typography>
              <VerifiedIcon sx={{ fontSize: 18, color: colors.purple[600] }} />
            </Stack>

            <Typography variant="caption" sx={{ color: colors.slate[600], display: "block" }}>
              Official Co-Host & Platform Beneficiary Partner
            </Typography>

            {/* Address */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  color: colors.slate[800],
                  bgcolor: "#ffffff",
                  px: 1,
                  py: 0.25,
                  borderRadius: borderRadiusPx.sm,
                  border: `1px solid ${colors.purple[200]}`,
                }}
              >
                {partner.partnerAddress}
              </Typography>

              <Tooltip title={copied ? "Copied!" : "Copy Partner Address"}>
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={{ p: 0.25, color: colors.slate[400], "&:hover": { color: colors.slate[800] } }}
                >
                  {copied ? (
                    <CheckIcon sx={{ fontSize: 14, color: colors.success.main }} />
                  ) : (
                    <CopyIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Tooltip>

              {explorerUrl && (
                <Tooltip title={EXPLORER_LABEL}>
                  <IconButton
                    size="small"
                    component="a"
                    href={explorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ p: 0.25, color: colors.slate[400], "&:hover": { color: colors.purple[600] } }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Stack>

        {/* Website Action Link */}
        {websiteUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<GlobeIcon />}
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            component="a"
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: colors.purple[700],
              borderColor: colors.purple[300],
              bgcolor: "#ffffff",
              "&:hover": {
                borderColor: colors.purple[500],
                bgcolor: colors.purple[100],
              },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: borderRadiusPx.md,
            }}
          >
            Visit Partner Website
          </Button>
        )}
      </Stack>
    </Card>
  );
};

LotteryPartnerCard.propTypes = {
  partner: PropTypes.object,
  partnerLogoUrl: PropTypes.string,
};

export default LotteryPartnerCard;
